import type { IAnalyticsEventFormField } from '@sravni/cosago-react-library/lib/types';
import { type FC, type ReactNode, useCallback, useMemo } from 'react';

import { isSatisfyOption, useAppDispatch, useAppSelector } from 'shared/lib';
import {
  sendEventChangeFormFieldDefaultParam,
  sendEventFillFormField,
  SendEventWrapper,
} from 'shared/lib/sendGAEvents';
import { useUpdateStateBySelector } from 'shared/lib/useUpdateStateBySelector';

import {
  CreditPurposeFormValidationSchema,
  selectCreditPurposeInfoData,
  type TCreditPurposeFormFields,
} from 'entities/creditForm';

import { FormFieldsControls } from '../../CreditPurposeInfo.config';
import { CreditCardPurposeInfoTexts, CreditPurposeInfoTexts } from '../../CreditPurposeInfo.texts';
import { submitCreditPurposeForm } from '../../lib/submitCreditPurposeForm';
import { selectCreditPurposeFormViewOptions } from '../../model/creditPurposeFormView.selectors';
import { CreditPurposeInfoViewHoc } from '../FormBody/FormBody';

export type TCreditPurposeFormProps = IFormPopup & {
  shouldRestoreAdditionalData?: boolean;
  productCardBlock?: ReactNode;
  productTabTitle?: string;
};

export const Form: FC<TCreditPurposeFormProps> = ({
  isLoading,
  isDialog = false,
  onClose,
  setHeader,
  isFormForceOpened,
  onFormSubmit,
  shouldRestoreAdditionalData,
  stepIndex,
  formId,
  submitButtonText = 'Далее',
  isAnyFormStepOpenedInPopup,
  analyticsOverrides,
  productCardBlock,
  productTabTitle,
}) => {
  const dispatch = useAppDispatch();
  const [formData] = useUpdateStateBySelector(selectCreditPurposeInfoData);
  const viewOptions = useAppSelector(selectCreditPurposeFormViewOptions);
  const validationSchema = useMemo(() => CreditPurposeFormValidationSchema(viewOptions), [viewOptions]);

  const formLabels = useMemo(
    () => (viewOptions.isCreditCardForm ? CreditCardPurposeInfoTexts : CreditPurposeInfoTexts),
    [viewOptions.isCreditCardForm],
  );

  const handleData = async (actualData: TCreditPurposeFormFields, type: string) => {
    if (type === 'fullFilled') {
      dispatch(submitCreditPurposeForm(actualData, analyticsOverrides));
      onFormSubmit?.(actualData, isDialog);
    }
    if (isDialog) {
      onClose?.(true);
    }
  };

  const sendAnalyticsEvent = useCallback(
    ({ fieldName }: IAnalyticsEventFormField) => {
      const isParameter = isSatisfyOption(['amount', 'period', 'purpose'], fieldName);
      if (isParameter) {
        sendEventChangeFormFieldDefaultParam(
          stepIndex + 1,
          CreditPurposeInfoTexts[fieldName as keyof TCreditPurposeFormFields],
          analyticsOverrides,
        );
      } else {
        sendEventFillFormField(
          stepIndex + 1,
          CreditPurposeInfoTexts[fieldName as keyof TCreditPurposeFormFields],
          true,
          analyticsOverrides,
        );
      }
    },
    [stepIndex, analyticsOverrides],
  );

  return (
    <SendEventWrapper sendEvent={sendAnalyticsEvent}>
      <CreditPurposeInfoViewHoc
        isDialog={isDialog}
        onDataChanged={handleData}
        defaultData={formData}
        validationSchema={validationSchema}
        isLoading={isLoading}
        additionalProps={{
          setHeader,
          isFormForceOpened,
          shouldRestoreAdditionalData: !!shouldRestoreAdditionalData,
          formId,
          isAnyFormStepOpenedInPopup,
          analyticsOverrides,
          productCardBlock,
          productTabTitle,
        }}
        formLabels={formLabels}
        formFieldsControls={FormFieldsControls}
        submitButtonText={submitButtonText}
      />
    </SendEventWrapper>
  );
};
